import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  GridSize,
  GridSpacing,
  IconButton,
  InputAdornment,
  makeStyles,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import React, { ReactNode, useEffect, useRef, useState } from 'react'

import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Info from '@material-ui/icons/Info'
import { InfoOutlined } from '@material-ui/icons'
import { Autocomplete, createFilterOptions } from '@material-ui/lab'

import clsx from 'clsx'
import { EnumPriceCalculationType } from '../../../api/Argentia/enums/priceCalculationType'
import {
  enumKeyToValue,
  enumKeyToValueString,
  enumToSelectOptionArray,
  enumValueToKey
} from '../../../helpers'
import { styles } from './priceExpressionValidator.styles'

import { DataType, Form, FormOptions, Section, useNotify } from '@globaleris/ui.library'

import { useTranslation } from 'react-i18next'
import { EnumPrecioUnitario } from '../../../api/oData/enums/enumPrecioUnitario'

import {
  getActivePrice,
  getCustomerDomain,
  getPersistedCompatibleCalculationType,
  getPersistedOperands,
  notifyError,
  storeCalculationTypes,
  storeOperands
} from '../../../redux'
import {
  fetchOperandsList,
  fetchPricesList,
  fetchPriceTypeCalculationTypeCompatibility,
  fetchPriceTypeCompatibility,
  fetchValidateExpressionType
} from './priceExpressionValidator.service'

import { Operand } from '../../../api/Argentia/endpoints/operands/model/operand'
import { PriceCalculationTypeCompatibility } from '../../../api/Argentia/endpoints/pricing/model/priceType'
import { EnumCommodity, EnumRedondeo } from '../../../api/Argentia/enums'
import { EnumTipoOperando } from '../../../api/Argentia/enums/tipoOperando'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Refresh } from '@material-ui/icons'
import moment from 'moment'
import { useHistory } from 'react-router'
import { EnumPriceType } from '../../../api/oData/enums/enumPriceType'
import { useIsFirstRender } from '../../../hooks/useIsFirstRender'
import { NonAuthRoutes } from '../../../model'

export interface PriceCalculationTypeOption {
  priceCalculationType: EnumPriceCalculationType
  label?: string
}

interface ErrorMsg {
  errorType: string
  operand?: string
}

export namespace ClearableInputs {
  export const PRICE_UNIT = 'PRICE_UNIT'
  export const ROUNDING = 'ROUNDING'
}
export type ClearableInputsType = (typeof ClearableInputs)[keyof typeof ClearableInputs]

export interface PriceExpressionValidatorProps {
  show?: boolean
  readOnly?: boolean
  disablePriceUnit?: boolean
  title?: string
  priceType: EnumPriceType
  commodity: EnumCommodity
  onSelectValidExpression: (priceExpression: string | undefined) => void
  onSelectPriceCalculationType: (priceCalculationType: EnumPriceCalculationType) => void
  onSelectPriceUnit: (priceUnit: EnumPrecioUnitario) => void
  onSelectRounding?: (rounding: EnumRedondeo) => void
  onSelectStartDate?: (startDate: string) => void
  onSelectEndDate?: (endDate: string | null) => void
  onIsValid: (isValid: boolean) => void
  showValidationHelper?: boolean
  onFormValid?: (FormValid: boolean) => void
  onFocusExpressionInput?: () => void
  priceVersion?: number
  // If activePriceCalculationType === null => types buttons won't be selected.
  // If activePriceCalculationType === undefined => first type button will be selected by default.
  activePriceCalculationType?: EnumPriceCalculationType | null
  activePriceUnit?: EnumPrecioUnitario
  activeExpression?: string
  activeExpressionIsValid?: boolean
  activeRounding?: EnumRedondeo
  activeVersionStartDate?: string
  activeVersionEndDate?: string | null
  enabled?: boolean
  inactive?: boolean
  isLoading?: boolean
  expressionInputSize?: GridSize
  priceUnitInputSize?: GridSize
  elevation?: number
  price?: any
  versionsButton?: boolean
  showVersion?: boolean
  showRounding?: boolean
  showDates?: boolean
  showPriceUnit?: boolean
  spacing?: GridSpacing
  modal?: boolean
  active?: boolean
  minVersionStartDate?: Date
  disableStartDate?: boolean
  resetForm?: boolean
  clearablePriceUnit?: boolean
  clearableRounding?: boolean
  onIsLoading?: (isLoading: boolean) => void
}

export interface PriceExpressionFieldsModel {
  priceExpression?: string
  priceUnit?: EnumPrecioUnitario
  priceVersion?: number
  rounding?: EnumRedondeo
  versionStartDate?: string
  versionEndDate?: string | null
}

interface Price {
  id: number
  code: string
}

interface expressionNode {
  code: string
}

interface currentOperand {
  isComposing: boolean
  operand?: Operand | null
}

interface datesFormState {
  startDate: string
  endDate?: string | null
}

export const PriceExpressionValidator: React.FC<PriceExpressionValidatorProps> = ({
  show = true,
  readOnly = false,
  disablePriceUnit = false,
  title,
  priceType,
  commodity,
  clearablePriceUnit = false,
  clearableRounding = false,
  activePriceUnit = undefined,
  activePriceCalculationType = undefined,
  activeExpression = undefined,
  activeExpressionIsValid,
  activeRounding = undefined,
  activeVersionStartDate = undefined,
  activeVersionEndDate = undefined,
  priceVersion = 1,
  onSelectValidExpression,
  onSelectPriceCalculationType,
  onSelectPriceUnit,
  onSelectRounding,
  onSelectStartDate,
  onSelectEndDate,
  onIsValid,
  onFormValid,
  onIsLoading,
  onFocusExpressionInput,
  enabled = true,
  inactive = false,
  expressionInputSize = 8,
  priceUnitInputSize = 7,
  elevation,
  price,
  versionsButton = false,
  showVersion = false,
  showRounding = false,
  showDates = false,
  showPriceUnit = false,
  spacing = 2,
  modal = false,
  active = true,
  minVersionStartDate,
  disableStartDate = false,
  resetForm = false,
  showValidationHelper = false
}) => {
  const history = useHistory()
  const [t] = useTranslation(['priceExpressionValidator', 'globalerisLibrary', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const isFirstRender = useIsFirstRender()
  const { info, error } = useNotify()

  const inputRef = useRef<HTMLInputElement>()
  const validateButtonRef = useRef<HTMLButtonElement>(null)
  const [caretPosition, setCaretPosition] = useState<number | null>(null)

  const updateCaretPosition = () => {
    inputRef.current && setCaretPosition(inputRef.current.selectionStart)
  }

  const [loadingTypesOptions, setLoadingTypesOptions] = useState<boolean>(true)
  const [loadingTypesOptionsFailed, setLoadingTypesOptionsFailed] = useState<boolean>(false)
  const [calculationTypeOptions, setCalculationTypeOptions] = useState<
    PriceCalculationTypeCompatibility[]
  >([])
  const [calculationTypeCompatibleOptions, setCalculationTypeCompatibleOptions] = useState<any[]>(
    []
  )

  const [activeType, setActiveType] = useState<EnumPriceCalculationType | null | undefined>(
    activePriceCalculationType
  )
  const [priceExpressionFields, setPriceExpressionFields] = useState<PriceExpressionFieldsModel>({
    priceVersion: (price && price?.version) || priceVersion,
    priceUnit: activePriceUnit,
    rounding: activeRounding,
    versionStartDate: activeVersionStartDate,
    versionEndDate: activeVersionEndDate,
    priceExpression: activeExpression && activeExpression
  })
  const [expressionAutocomplete, setExpressionAutocomplete] = useState<
    (expressionNode | Operand | Price)[]
  >([])
  const [currentNode, setCurrentNode] = useState<string[]>([])

  const [isExpressionModified, setIsExpressionModified] = useState<boolean>(false)
  const [isValid, setIsValid] = useState<boolean | undefined>(activeExpressionIsValid)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const [open, setOpen] = useState<boolean>(false)
  const [options, setOptions] = useState<any[]>([])
  const [pricesOptions, setPricesOptions] = useState<Price[]>([])
  const [operandsOptions, setOperandsOptions] = useState<Operand[]>([])
  const [operands, setOperands] = useState<Operand[]>([])

  const [dates, setDates] = useState<Partial<datesFormState>>({
    startDate: activeVersionStartDate,
    endDate: activeVersionEndDate
  })

  const [currentOperand, setCurrentOperand] = useState<currentOperand>({
    isComposing: false
  })
  const [currentParam, setCurrentParam] = useState<number | null>(null)

  const [editableNodeIdx, setEditableNodeIdx] = useState<number | null>(null)
  const [inputValue, setInputValue] = useState<string | undefined>(activeExpression || '')

  const priceDetails = price ? price : getActivePrice()
  // Form Config starts //

  const datesForm: Section[] = [
    {
      inputs: [
        {
          name: 'startDate',
          label: t('priceExpressionValidator:input.label.startDate'),
          type: DataType.DATE,
          inputSize: 5,
          required: true,
          readOnly: readOnly ? true : disableStartDate ? true : false,
          onChange: value => handleStartDate(String(value))
        },
        {
          name: 'endDate',
          label: t('priceExpressionValidator:input.label.endDate'),
          type: DataType.DATE,
          readOnly: readOnly,
          inputSize: 5,
          onChange: value => handleEndDate(String(value))
        }
      ]
    }
  ]

  const datesFormOptions: FormOptions = {
    elevation: 0,
    columns: 2,
    analytics: {
      customer: getCustomerDomain(),
      view: t('translation:components.priceExpressionValidator.analytics')
    }
  }

  // Form Config ends //

  // Functions starts //

  const clearExpressionInput = (): void => {
    setExpressionAutocomplete([])
    setInputValue('')
    setPriceExpressionFields({ ...priceExpressionFields, priceExpression: '' })
    setCurrentNode([])
    setIsValid(undefined)
    setErrorMsg(undefined)
    setCurrentOperand({
      isComposing: false
    })
  }

  const getOptionLabel = (typeOption: PriceCalculationTypeOption) => {
    return typeOption
      ? t(`enums:EnumPriceCalculationTypes.${enumKeyToValue(EnumPriceCalculationType, typeOption)}`)
      : ''
  }

  const getErrorType = (errorMsg: string): ErrorMsg | undefined => {
    if (errorMsg === 'This price must have at least one Complex Operand.') {
      return { errorType: 'complexOperandNotFound' }
    } else if (errorMsg === 'This price must have at least two operands.') {
      return { errorType: 'missingOperand' }
    } else if (errorMsg === 'This price only allows a single constant operand.') {
      return { errorType: 'onlyConstantAllowed' }
    } else if (errorMsg === 'Simple prices cannot be constants.') {
      return { errorType: 'constantsNotAllowed' }
    } else if (errorMsg === 'Error - The operand has not associated rule.') {
      return { errorType: 'cannotParse' }
    } else if (errorMsg === 'Invalid parameters number.') {
      return { errorType: 'invalidParamsCount' }
    } else if (
      errorMsg.split(' ').slice(0, -1).join(' ') === 'This price does not allow operand type'
    ) {
      return {
        errorType: 'operandTypeNotAllowed'
      }
    } else if (errorMsg.split(' ').slice(0, 3).join(' ') === 'Cannot parse expression') {
      return {
        errorType: 'cannotParse'
      }
    } else if (errorMsg.split(' ').slice(0, 3).join(' ') === 'Cannot find identifier') {
      return {
        errorType: 'priceNotFound',
        operand: errorMsg.split(' ').slice(3, 4).join(' ')
      }
    }
  }

  const fetchValidateExpression = (
    expression: string,
    priceCalculationType: EnumPriceCalculationType
  ) => {
    setIsLoading(true)
    setIsValid(undefined)
    setErrorMsg(undefined)
    fetchValidateExpressionType(expression, priceCalculationType).then(response => {
      setIsLoading(false)
      if (response.success) {
        if (response.data.success) {
          onSelectValidExpression(expression)
          setPriceExpressionFields({ ...priceExpressionFields, priceExpression: expression })
          setIsValid(true)
          onIsValid(true)
          setCurrentOperand({ isComposing: false })
        } else {
          setErrorMsg(response.data.errors[0])
          onSelectValidExpression(undefined)
          setIsValid(false)
          onIsValid(false)
        }
      } else {
        onSelectValidExpression(undefined)
        setIsValid(undefined)
        onIsValid(false)
        notifyError(t('priceExpressionValidator:messages.errorApi'))
      }
    })
  }

  const fetchOperands = async () => {
    const response = await fetchOperandsList()
    if (response.success) {
      const operandsRes = response.data.map(operand => ({
        ...operand,
        code: operand.codOperando,
        category: 'Operandos'
      }))
      if (activeType === EnumPriceCalculationType.Simple) {
        const mappedOperands = operandsRes.filter(
          operand => operand.operandType === enumKeyToValue(EnumTipoOperando, EnumTipoOperando.BAS)
        )
        setOperandsOptions(mappedOperands)
      } else if (activeType === EnumPriceCalculationType.Complejo) {
        setOperandsOptions(operandsRes)
      } else {
        setOperandsOptions([])
      }
      // We save all the operands so next time we will filter them from here to avoid doing the same fetch.
      setOperands(operandsRes)
      storeOperands(operandsRes)
      // storeOperands
    }
  }

  const getOperands = async () => {
    if (activeType === EnumPriceCalculationType.Fijo) {
      setOperandsOptions([])
      return
    } else if (operands.length > 0) {
      // We have already made the operands fetch.
      if (activeType === EnumPriceCalculationType.Simple) {
        setOperandsOptions(
          operands.filter(
            operand =>
              operand.operandType === enumKeyToValue(EnumTipoOperando, EnumTipoOperando.BAS)
          )
        )
      } else if (activeType === EnumPriceCalculationType.Complejo) {
        setOperandsOptions(operands)
      } else {
        setOperandsOptions([])
      }
    } else {
      // We haven't made the operands fetch yet.
      await fetchOperands()
    }
  }

  const getPrices = async (priceTypes: EnumPriceType[]) => {
    const response = await fetchPricesList(commodity, priceTypes)
    if (response.success) {
      // setLoading(false);
      const prices = response.data.map(price => ({ ...price, category: 'Precios' }))
      const pricesOptions = priceDetails ? prices.filter(p => p.code !== priceDetails.code) : prices
      setOptions(pricesOptions)
      setPricesOptions(pricesOptions)
    } else {
      // setLoading(false);
    }
  }

  const getCompatiblePrices = async (priceType: EnumPriceType) => {
    // setLoading(true);
    const priceTypesResponse = await fetchPriceTypeCompatibility(priceType)
    if (priceTypesResponse.success) {
      const priceTypesList = priceTypesResponse.data.priceTypeComponent
      if (priceTypesList.length > 0) {
        await getPrices(priceTypesList)
      }
    }
  }

  const getOperandExpression = (operandName: string | undefined): ReactNode | null => {
    const getOperandObj = operands.find(o => o.code === operandName)
    const opParamsArray = getOperandObj
      ? getOperandObj?.parametersList.map(param => {
          if (param.order !== currentParam) {
            return param.order === getOperandObj?.parametersList.length
              ? `${param.parameterCode}`
              : `${param.parameterCode};`
          } else
            return param.order === getOperandObj?.parametersList.length ? (
              <b>{param.parameterCode}</b>
            ) : (
              <b>{param.parameterCode};</b>
            )
        })
      : null
    return opParamsArray ? (
      <span>
        {`${operandName}(`}
        {opParamsArray.map(param => param)}
        {`)`}
      </span>
    ) : null
  }

  const renderErrorMessage = (): '' | ReactNode => {
    const error = errorMsg ? getErrorType(errorMsg) : null
    if (inputValue !== '') {
      return (
        <span className={classes.errorMessage}>
          {t(`priceExpressionValidator:messages.apiErrors.${error?.errorType}`, {
            operand: error?.operand
          })}
        </span>
      )
    }
  }

  const getHelperText = (): ReactNode => {
    if (!isLoading) {
      // If isExpressionModified is false, means the expression is fetched from DDBB, thus, validated and message must not be shown.
      if (!isExpressionModified) {
        return ''
      } else if (isValid === undefined) {
        if (activeType === EnumPriceCalculationType.Fijo) {
          return 'Inserte un número. No se permiten operandos lógicos ni referencias a Precios existentes.'
        } else if (activeType === EnumPriceCalculationType.Simple) {
          return 'Puede utilizar números, operandos lógicos (+ - * /) y Precios. Ejemplo: MIBGAS+10'
        } else if (activeType === EnumPriceCalculationType.Complejo) {
          if (currentOperand.isComposing) {
            const exp = getOperandExpression(currentOperand.operand?.codOperando)
            return exp ? exp : ''
          } else {
            return ''
          }
        } else return ''
      } else if (isValid) {
        if (currentOperand.isComposing) {
          const exp = getOperandExpression(currentOperand.operand?.codOperando)
          return exp ? exp : <span className={classes.successMessage}>Expresión válida.</span>
        } else {
          return <span className={classes.successMessage}>Expresión válida.</span>
        }
      } else if (!isValid) {
        return renderErrorMessage()
      }
    } else {
      return 'Comprobando expresión...'
    }
  }

  const digExpression = (exp: string, separators?: string[]): string[] => {
    // const regExpression = separators

    return exp.split(new RegExp('([+|-|*|/|(|)|;])'))
  }

  const getCurrentOperand = () => {
    // we process the input string from caret/cursor position down to first character, from right to left <----
    // We will use the parenthesis to know if an operand expression is starting -from right to left-, ), or ending (.
    // We also will check and count for ; , to register current parameter position.
    let countInnerOperand = 0
    let countParams = 0
    let isInnerOperand = false
    if (inputValue && inputValue !== '' && caretPosition) {
      for (var i = caretPosition - 1; i >= 0; i--) {
        // str.charAt(i)
        let hasOperand = false
        const char = inputValue.charAt(i)
        if (char === ')') {
          // Start of inner Operand
          isInnerOperand = true
          countInnerOperand += 1
        } else if (char === ';' && !isInnerOperand) {
          // We count the ; if we are not inside an 'inner' operand.
          countParams += 1
        } else if (char === '(') {
          if (countInnerOperand === 0) {
            // End of current operand Expression
            const digestedExp = digExpression(inputValue)
            let charCount = 0
            for (const arg in digestedExp) {
              charCount += digestedExp[arg].length
              if (charCount < i) {
                continue
              } else if (charCount === i) {
                const operandName = digestedExp[arg]
                if (operandName) {
                  hasOperand = true
                  setCurrentOperand({
                    isComposing: true,
                    operand: getOperandObj(operandName) || null
                  })
                  setIsValid(undefined)
                } else {
                  setCurrentOperand({
                    isComposing: false,
                    operand: null
                  })
                }
              }
            }
            break
          } else {
            // End of inner Operand
            countInnerOperand -= 1
            if (countInnerOperand === 0) {
              isInnerOperand = false
            }
          }
        }
        !hasOperand &&
          setCurrentOperand({
            isComposing: false,
            operand: null
          })
      }
    }
    setCurrentParam(countParams + 1)
  }

  // Functions ends //

  // Handlers starts //

  const handleClearClick = (inputName: ClearableInputsType) => {
    switch (inputName) {
      case ClearableInputs.PRICE_UNIT: {
        handlePriceUnitChange(null)
        break
      }
      case ClearableInputs.ROUNDING: {
        handleRounding(null)
        break
      }
    }
  }

  const priceCalculationTypeChange = (priceCalculationType: EnumPriceCalculationType | null) => {
    setErrorMsg(undefined)
    !isFirstRender && setIsValid(undefined)
    setActiveType(priceCalculationType)
    priceCalculationType && onSelectPriceCalculationType(priceCalculationType)
  }

  const handlePriceUnitChange = (inputValue: EnumPrecioUnitario | null) => {
    setPriceExpressionFields({
      ...priceExpressionFields,
      priceUnit: inputValue ? enumValueToKey(EnumPrecioUnitario, inputValue) : null
    })
    onSelectPriceUnit(enumValueToKey(EnumPrecioUnitario, inputValue))
  }

  const handleRounding = (inputValue: EnumRedondeo | null) => {
    setPriceExpressionFields({
      ...priceExpressionFields,
      rounding: inputValue ? enumValueToKey(EnumRedondeo, inputValue) : null
    })
    onSelectRounding && onSelectRounding(enumValueToKey(EnumRedondeo, inputValue))
  }

  const handleStartDate = (inputValue: string) => {
    const newDate = moment(inputValue).format('YYYY-MM-DD').toString()

    // minDate is First Version's start Date. To avoid problems with Prices Engine, this date IS FIXED when price is created and can't be changed
    const minDate = moment(minVersionStartDate).format('YYYY-MM-DD').toString()

    if (newDate.toLowerCase() !== 'invalid date') {
      if (minVersionStartDate && moment(inputValue).isBefore(moment(minDate), 'days')) {
        notifyError(t(`priceExpressionValidator:messages.invalidStartDate`))
        setDates({ ...dates, startDate: priceExpressionFields.versionStartDate })
      } else if (dates.endDate && moment(inputValue).isSameOrAfter(moment(dates.endDate), 'days')) {
        const newEndDate = moment(inputValue).add(1, 'day').toDate().toString()
        setPriceExpressionFields({
          ...priceExpressionFields,
          versionStartDate: newDate,
          versionEndDate: newEndDate
        })
        setDates({ ...dates, startDate: newDate, endDate: newEndDate })
        onSelectStartDate && onSelectStartDate(newDate)
        onSelectEndDate && onSelectEndDate(newEndDate)
      } else {
        setPriceExpressionFields({
          ...priceExpressionFields,
          versionStartDate: newDate
        })
        setDates({ ...dates, startDate: newDate })
        onSelectStartDate && onSelectStartDate(newDate)
      }
    }
  }

  const handleEndDate = (inputValue: string) => {
    const newDate = moment(inputValue).format('YYYY-MM-DD').toString()
    const dateIsValid = moment(inputValue).isValid()
    if (dateIsValid) {
      if (dates.startDate && moment(inputValue).isSameOrBefore(moment(dates.startDate), 'days')) {
        const newEndDate = moment(priceExpressionFields.versionStartDate)
          .add(1, 'day')
          .toDate()
          .toString()
        setPriceExpressionFields({
          ...priceExpressionFields,
          versionEndDate: newEndDate
        })
        setDates({ ...dates, endDate: newEndDate })
        onSelectEndDate && onSelectEndDate(newEndDate)
      } else {
        setPriceExpressionFields({
          ...priceExpressionFields,
          versionEndDate: newDate
        })
        setDates({ ...dates, endDate: newDate })
        onSelectEndDate && onSelectEndDate(newDate)
      }
    } else if (priceExpressionFields.versionEndDate) {
      setPriceExpressionFields({
        ...priceExpressionFields,
        versionEndDate: null
      })
      onSelectEndDate && onSelectEndDate(null)
    }
  }

  const getNodeByCaretPosition = (
    movePoss?: number,
    expArray?: (Price | Operand | expressionNode)[]
  ) => {
    const position = movePoss && caretPosition ? caretPosition + movePoss : caretPosition
    const expressionArray = expArray ? expArray : expressionAutocomplete
    let charsCount = 0
    if (position) {
      for (const nodeIdx in expressionArray) {
        const nodeName = expressionArray[nodeIdx].code
        charsCount += nodeName ? nodeName.length : 0
        if (charsCount >= position) {
          return expressionArray[nodeIdx]
        }
      }
    } else {
      return null
    }
  }

  const getUpdatedExpressionArray = (stringValue: string) => {
    // value is the string we received from the event which has the entire updated input value
    const parsedString = digExpression(stringValue).filter(str => str !== '')
    const processedArray = parsedString.map(name => {
      const priceObj = pricesOptions.filter(opt => opt.code === name)[0]
      const operandObj = getOperandObj(name)
      if (isPrice(name) && priceObj) {
        return priceObj
      }
      if (isOperand(name) && operandObj) {
        return operandObj
      }
      return { code: name }
    })
    return processedArray
  }

  const handleInputChange = (e, val) => {
    const value = e?.target && e.target.value
    const newValue = e?.target && e.nativeEvent.data

    e && setInputValue(value ?? '')

    if (value === '') {
      // User has manually deleted the string
      setExpressionAutocomplete([])
      setInputValue('')
      setPriceExpressionFields({ ...priceExpressionFields, priceExpression: '' })
      setCurrentNode([])
      setIsValid(undefined)
      setErrorMsg(undefined)
      setCurrentOperand({
        isComposing: false
      })
    } else if (e.nativeEvent.inputType === 'deleteContentBackward') {
      const updatedExpressionArray = getUpdatedExpressionArray(value)
      // User hit backspace
      setIsValid(undefined)
      setErrorMsg(undefined)
      if (caretPosition && value && caretPosition < value.length) {
        const editingNode = getNodeByCaretPosition(-1, updatedExpressionArray)?.code || ''
        if (isOperand(editingNode) && editingNode !== ')') {
          // Case when node was completely deleted.
          setCurrentNode([])
        } else {
          setCurrentNode([editingNode])
        }
        return
      }
      if (currentNode.length === 0) {
        if (updatedExpressionArray && updatedExpressionArray.length > 0) {
          // If we had nodes but no currentNode (unfinished node), we grab the last one, trim the last character and set it as currentNode, so options will be filtered by this node.
          // Deletion of last node from array.
          if (caretPosition && inputValue && caretPosition < inputValue.length) {
            const editingNode = getNodeByCaretPosition()
            const newNode = editingNode?.code.substring(0, editingNode?.code.length - 1) || ''
            setCurrentNode([newNode])
          } else {
            const newNode =
              updatedExpressionArray &&
              updatedExpressionArray[updatedExpressionArray.length - 1].code
            const newExpressionArray = updatedExpressionArray.filter(
              (_, index) => index !== updatedExpressionArray.length - 1
            )
            newNode && setCurrentNode([newNode.substring(0, newNode.length - 1)])
            setExpressionAutocomplete(newExpressionArray)
          }
        }
      } else {
        // We trim last character from currentNode.
        const newNode = currentNode[0].substring(0, currentNode[0].length - 1)
        setCurrentNode([newNode])
      }
    } else if (newValue === '(' && isOperandComplex(currentNode[0])) {
      // User finished typing valid complex operand.
      const newOperandNode = getOperandObj(currentNode[0]) || { code: '' }
      const newOperandNodeClose = getOperandObj('(') || { code: '' }
      setExpressionAutocomplete([...expressionAutocomplete, newOperandNode, newOperandNodeClose])
      setCurrentOperand({
        isComposing: true,
        operand: getOperandObj(currentNode[0])
      })
      setCurrentNode([])
      setIsValid(undefined)
      return
    } else if (newValue === ')' && currentOperand.isComposing) {
      // Closing Complex Operand composition
      const newOperandNode = getOperandObj(')') || { code: '' }
      if (currentNode[0]) {
        setExpressionAutocomplete([
          ...expressionAutocomplete,
          { code: currentNode[0] },
          newOperandNode
        ])
      } else {
        setExpressionAutocomplete([...expressionAutocomplete, newOperandNode])
      }

      setCurrentOperand({ isComposing: false })
      setCurrentNode([])
      return
    } else if (isOperand(newValue) && expressionAutocomplete) {
      if (expressionAutocomplete[expressionAutocomplete.length - 1]?.code !== newValue) {
        // New input is an operand and different as last confirmed node.
        const newOperandNode = operandsOptions.filter(opt => opt.code === newValue)
        const newExpNode = currentNode.length > 0 ? currentNode[0] : ''
        // const prevValue = currentNode.length > 0 ? currentNode[0].slice(0, -newValue.length) : '';
        const newNode = isConstant(newExpNode)
          ? { code: newExpNode }
          : isPrice(newExpNode)
            ? pricesOptions.filter(opt => opt.code === newExpNode)[0]
            : { code: newExpNode }
        newNode?.code && newNode.code !== ''
          ? setExpressionAutocomplete([...expressionAutocomplete, newNode, ...newOperandNode])
          : setExpressionAutocomplete([...expressionAutocomplete, ...newOperandNode])
        setCurrentNode([])
      } else {
        // new input is an operand AND EQUAL AS LAST NODE, we reset the currentNode except for '(' and ')' which we allowed to be double like '((' and '))'
        if (newValue !== '(' && newValue !== ')') {
          setCurrentNode([])
        }
      }
    } else if (!newValue && value) {
      // User paste an expression on input.
      const updatedExpressionArray = getUpdatedExpressionArray(value)
      setExpressionAutocomplete(updatedExpressionArray)
    } else if (newValue === ';') {
      if (currentNode[0]) {
        setExpressionAutocomplete([
          ...expressionAutocomplete,
          { code: currentNode[0] },
          { code: newValue }
        ])
      } else {
        setExpressionAutocomplete([...expressionAutocomplete, { code: newValue }])
      }
      setCurrentNode([])
      return
    } else {
      if (activeType === EnumPriceCalculationType.Simple) {
        const nodeString = newValue
          ? [[...currentNode, newValue].join('')]
          : currentNode && currentNode[0] === null
            ? expressionAutocomplete.length === 0
              ? []
              : [expressionAutocomplete[expressionAutocomplete.length - 1].code.slice(0, -1)]
            : currentNode && [currentNode[0].slice(0, -1)]
        setCurrentNode(nodeString)
      } else if (activeType === EnumPriceCalculationType.Complejo) {
        const nodeString = newValue
          ? [[...currentNode, newValue].join('')]
          : currentNode && currentNode[0] === null
            ? expressionAutocomplete.length === 0
              ? []
              : [expressionAutocomplete[expressionAutocomplete.length - 1].code.slice(0, -1)]
            : currentNode && [currentNode[0].slice(0, -1)]
        setCurrentNode(nodeString)
      }
    }
  }

  const handleChangeAutocomplete = (e, value) => {
    const clickedOption: string | undefined = e?.target && e.target.textContent
    const b = e?.target && e.nativeEvent.data

    if (value.length === 0) {
      // Clear button
      clearExpressionInput()
      return
    }

    if (activeType !== EnumPriceCalculationType.Complejo) {
      if (value.length !== 1) {
        // Exists an unfinished expression typed but was selected an option from dropdown.
        setExpressionAutocomplete([...expressionAutocomplete, value[1]])
        setInputValue(expressionString([...expressionAutocomplete, value[1]]))
        setCurrentNode([])
      } else if (value.length === 1) {
        setExpressionAutocomplete([...expressionAutocomplete, value[0]])
        setInputValue(expressionString([...expressionAutocomplete, value[0]]))
        setCurrentNode([])
      } else {
        if (value !== expressionAutocomplete && editableNodeIdx === null) {
          if (value.length === 0) {
            // Clear button
            setExpressionAutocomplete([])
            setInputValue('')
          } else if (value.length < expressionAutocomplete.length) {
            // Means user clicked on a already selected option, MUI component tends to delete it.
            // Value array is the expression array modified by the user selection.
            // We need to get the selected option comparing both arrays.
            const selectedOption = expressionAutocomplete.filter(
              (
                i => a =>
                  a !== value[i] || !++i
              )(0)
            )
            setExpressionAutocomplete([...expressionAutocomplete, selectedOption[0]])
            setInputValue(expressionString([...expressionAutocomplete, selectedOption[0]]))
            setCurrentNode([])
          } else {
            setExpressionAutocomplete(value)
            setInputValue(value[0].code)
          }
        } else if (editableNodeIdx !== null) {
          // Edit mode. Selected option has to replace the previous node.
          let newExpression = [...expressionAutocomplete]
          if (value.length < expressionAutocomplete.length) {
            // Means user clicked on a already selected option, MUI component tends to delete it.
            const selectedOption = expressionAutocomplete.filter(
              (
                i => a =>
                  a !== value[i] || !++i
              )(0)
            )
            newExpression[editableNodeIdx] = selectedOption[0]
            setExpressionAutocomplete(newExpression)
            setEditableNodeIdx(null)
          } else {
            newExpression[editableNodeIdx] = value[value.length - 1]
            setExpressionAutocomplete(newExpression)
            setEditableNodeIdx(null)
          }
        }
      }
    } else {
      // COMPLEX EXPRESSION LOGIC HANDLER
      if (value.length === 1) {
        // Case when selected option with empty input (first selection)
        if (isOperandComplex(value[0].codOperando)) {
          // Operand is complex: We open () and reset currentNode.
          const newExpressionArray = [...expressionAutocomplete, value[0], getOperandObj('(')]
          setInputValue(expressionString(newExpressionArray))
          setExpressionAutocomplete(newExpressionArray)
          setCurrentOperand({
            isComposing: true,
            operand: getOperandObj(value[0].codOperando) || undefined
          })
          setCurrentNode([])
          setIsValid(undefined)
        } else {
          setExpressionAutocomplete([...expressionAutocomplete, value[0]])
          setInputValue(expressionString([...expressionAutocomplete, value[0]]))
          setCurrentNode([])
        }
        return
      } else if (value.length === 2) {
        // User selected an option with keyboard with an unfinished string (comes in value[0]), OR editing existing value
        if (caretPosition && inputValue && caretPosition < inputValue?.length) {
          // User has selected an option from the middle of the string, editing a pre-existing value.
          const updatedExpressionArray = getUpdatedExpressionArray(inputValue)
          const editingNode = getNodeByCaretPosition(0, updatedExpressionArray)
          if (editingNode) {
            const newExpressionArray = updatedExpressionArray.map(node => {
              if (node.code === editingNode?.code) {
                return value[1]
              } else return node
            })
            setInputValue(expressionString(newExpressionArray))
            setExpressionAutocomplete(newExpressionArray)
            setCurrentNode([])
          }
          return
        } else {
          if (isOperandComplex(value[1].codOperando)) {
            // Operand is complex: We open () and reset currentNode.
            const newExpressionArray = [...expressionAutocomplete, value[1], getOperandObj('(')]
            setInputValue(expressionString(newExpressionArray))
            setExpressionAutocomplete(newExpressionArray)
            setCurrentOperand({
              isComposing: true,
              operand: getOperandObj(value[1].codOperando) || undefined
            })
            setCurrentNode([])
            setIsValid(undefined)
            return
          } else {
            setExpressionAutocomplete([...expressionAutocomplete, value[1]])
            setInputValue(expressionString([...expressionAutocomplete, value[1]]))
            setCurrentNode([])
          }
          return
        }
      }
      if (clickedOption && isPrice(clickedOption)) {
        setExpressionAutocomplete([...expressionAutocomplete, value[1]])
        setInputValue(inputValue + clickedOption)
        setCurrentNode([])
        // setExpressionAutocomplete(newExpressionArray);
      } else if (isOperandComplex(value[0].codOperando)) {
        // Operand is complex: We open () and reset currentNode.
        setInputValue(inputValue + value[0].codOperando + '(')
        setExpressionAutocomplete([...expressionAutocomplete, value[0], getOperandObj('(')])
        setCurrentOperand({
          isComposing: true,
          operand: getOperandObj(value[0].codOperando) || undefined
        })
        setCurrentNode([])
        setIsValid(undefined)
      }
    }
  }

  const expressionString = (expressionArray: any[]): string => {
    const expressionStringArray = expressionArray.map(expression => expression.code || expression)
    return expressionStringArray.join('')
  }

  const handleBlurAutocomplete = event => {
    setCurrentNode([])
    const currentActiveType = activeType

    if (event.relatedTarget && event.relatedTarget.id === 'calculation-type-button') {
      // In case component triggered onBlur because user clicked on priceCalculationType buttons, we cancel this fetch: the new priceCalculationType will trigger a new fetch to validate the expression.
      return
    } else {
      if (
        !isLoading &&
        inputValue &&
        inputValue.length > 0 &&
        activeType &&
        currentActiveType === activeType
      ) {
        fetchValidateExpression(inputValue, activeType)
        setIsExpressionModified(true)
      }
    }
  }

  const handleFocusAutocomplete = event => {
    onFocusExpressionInput && onFocusExpressionInput()
  }
  // Handlers ends //

  const getCompatiblesCalculationTypes = async () => {
    let newType: EnumPriceCalculationType | null | undefined

    if (calculationTypeOptions.length === 0) {
      const persistedCompatibleCalculationTypes = getPersistedCompatibleCalculationType()

      if (persistedCompatibleCalculationTypes) {
        setCalculationTypeOptions(persistedCompatibleCalculationTypes)
        setLoadingTypesOptions(false)

        const compatibleTypes = persistedCompatibleCalculationTypes.find(
          p => p.priceType === enumKeyToValueString(EnumPriceType, +priceType)
        )

        newType =
          compatibleTypes &&
          activeType &&
          compatibleTypes.priceCalculationType.find(p => p === +activeType)

        compatibleTypes &&
          priceCalculationTypeChange(newType ?? compatibleTypes.priceCalculationType[0])
      } else {
        getPriceTypeCalculationTypeCompatibility()
      }
    } else {
      if (calculationTypeOptions && priceType) {
        const compatibleTypes = calculationTypeOptions.find(
          p => p.priceType === enumKeyToValueString(EnumPriceType, +priceType)
        )
        newType =
          activeType &&
          compatibleTypes &&
          compatibleTypes.priceCalculationType.find(p => p === +activeType)

        compatibleTypes &&
          priceCalculationTypeChange(newType ?? compatibleTypes.priceCalculationType[0])
      }
    }
  }

  const getPriceTypeCalculationTypeCompatibility = async () => {
    setLoadingTypesOptionsFailed(false)
    setLoadingTypesOptions(true)
    const response = await fetchPriceTypeCalculationTypeCompatibility().finally(() => {
      setLoadingTypesOptions(false)
    })

    if (response.success) {
      const compatibleCalculationTypes = response.data
      setCalculationTypeOptions(compatibleCalculationTypes)
      storeCalculationTypes(compatibleCalculationTypes)

      const compatibleTypes = compatibleCalculationTypes.find(
        p => p.priceType === enumKeyToValueString(EnumPriceType, +priceType)
      )
      const newType =
        compatibleTypes &&
        activeType &&
        compatibleTypes.priceCalculationType.find(p => p === +activeType)

      compatibleTypes &&
        priceCalculationTypeChange(newType ?? compatibleTypes.priceCalculationType[0])

      if (!newType && !compatibleTypes) {
        setLoadingTypesOptionsFailed(true)
        error(t('priceExpressionValidator:messages.apiErrors.compatibleCalculationTypes'))
      } else {
        setLoadingTypesOptionsFailed(false)
      }
    } else {
      setLoadingTypesOptionsFailed(true)
      error(t('priceExpressionValidator:messages.apiErrors.compatibleCalculationTypes'))
    }
  }
  // else {
  //         setLoadingTypesOptionsFailed(true)
  //         error(t('priceExpressionValidator:messages.apiErrors.compatibleCalculationTypes'))
  //       }
  // const setActiveTypeFromCompatibles = () => {

  // }

  // const setActiveTypeFromCompatibles = () => {

  // }

  // const setActiveTypeFromCompatibles = () => {

  // }

  // const setActiveTypeFromCompatibles = () => {

  // }

  // Hooks starts //

  useEffect(() => {
    if (active) {
      // Refresh operands options for the new priceCalculationType
      getOperands()

      // Expression needs to be validated again.
      // !isFirstRender && setIsValid(undefined);

      // Set selected PriceCalculationType.
      activeType && onSelectPriceCalculationType(activeType)

      // Validate the expression, only if it's not empty.
      if (
        inputValue &&
        inputValue.length > 0 &&
        activeType &&
        !isFirstRender &&
        isValid === undefined
      ) {
        fetchValidateExpression(inputValue, activeType)
        setIsExpressionModified(true)
      } else {
        setIsLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType, active, isFirstRender])

  useEffect(() => {
    if (active) {
      // Refresh compatible prices for the selected priceType.
      activeExpression === undefined && clearExpressionInput()
      getCompatiblesCalculationTypes()
      getCompatiblePrices(enumValueToKey(EnumPriceType, EnumPriceType[priceType]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceType, active, commodity])

  useEffect(() => {
    if (
      priceExpressionFields.priceExpression === '' ||
      priceExpressionFields.priceExpression === null
    ) {
      !isFirstRender && setIsValid(undefined)
      !isFirstRender && onIsValid(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceExpressionFields.priceExpression])

  useEffect(() => {
    isValid && priceExpressionFields.priceExpression
      ? onSelectValidExpression(priceExpressionFields.priceExpression)
      : onSelectValidExpression(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceExpressionFields.priceExpression, isValid])

  useEffect(() => {
    if (show && active) {
      activeType && onSelectPriceCalculationType(activeType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, active])

  useEffect(() => {
    setPriceExpressionFields({
      ...priceExpressionFields,
      versionStartDate: activeVersionStartDate
    })
    setDates({ ...dates, startDate: activeVersionStartDate })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVersionStartDate])

  useEffect(() => {
    setPriceExpressionFields({
      ...priceExpressionFields,
      versionEndDate: activeVersionEndDate
    })
    setDates({ ...dates, endDate: activeVersionEndDate })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVersionEndDate])

  useEffect(() => {
    if (
      activeExpressionIsValid &&
      isValid &&
      priceExpressionFields.priceExpression &&
      priceExpressionFields.priceUnit &&
      priceExpressionFields.rounding &&
      priceExpressionFields.versionStartDate &&
      dates.startDate
    ) {
      onFormValid && onFormValid(true)
    } else onFormValid && onFormValid(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceExpressionFields, activeExpressionIsValid, isValid, dates])

  useEffect(() => {
    if (active) {
      const persistedOperands = getPersistedOperands()
      persistedOperands && setOperands(persistedOperands)

      if (activeExpression && activeType === EnumPriceCalculationType.Simple) {
        const expArray = digExpression(activeExpression)
        const mappedExp = expArray.map(node => {
          if (isPrice(node)) {
            const nod = pricesOptions.filter(opt => opt.code === node)[0]
            return { ...nod, category: 'Precios' }
          } else if (isOperand(node)) {
            const nod = operandsOptions.filter(opt => opt.code === node)[0]
            return { ...nod, category: 'Operandos' }
          } else return { code: node }
        })
        setExpressionAutocomplete(mappedExp)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  useEffect(() => {
    GetCalculationTypeCompatibleOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceType, calculationTypeOptions, commodity])

  useEffect(() => {
    getCurrentOperand()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caretPosition])

  useEffect(() => {
    if (resetForm) {
      setPriceExpressionFields({
        priceVersion: (price && price?.version) || priceVersion,
        priceUnit: activePriceUnit,
        rounding: activeRounding,
        versionStartDate: activeVersionStartDate,
        versionEndDate: activeVersionEndDate,
        priceExpression: activeExpression && activeExpression
      })
      setIsValid(activeExpressionIsValid)
      setInputValue(activeExpression || '')
      setActiveType(activePriceCalculationType || EnumPriceCalculationType.Fijo)
      // onSelectPriceCalculationType(EnumPriceCalculationType.Fijo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetForm])
  // Hooks ends //

  const isConstant = (node): boolean => {
    if (node) {
      return +node ? true : false
    }
    return false
  }

  const isOperand = (node): boolean => {
    if (node && node !== '') {
      return operandsOptions.some(operand => operand.code.toLowerCase() === node.toLowerCase())
    }
    return false
  }

  const isOperandComplex = (node): boolean => {
    if (node && node !== '') {
      const op = operandsOptions.filter(
        operand => operand.codOperando.toLowerCase() === node.toLowerCase()
      )
      return (
        op.length > 0 &&
        enumValueToKey(EnumTipoOperando, op[0].operandType) === EnumTipoOperando.FCN
      )
    }
    return false
  }

  const getOperandObj = (node: string): Operand | null => {
    if (node && node !== '') {
      return operandsOptions.filter(
        operand => operand.codOperando.toLowerCase() === node.toLowerCase()
      )[0]
    }
    return null
  }

  const isPrice = (node): boolean => {
    if (node && node !== '') {
      return pricesOptions.some(price => price.code.toLowerCase() === node.toLowerCase())
    }
    return false
  }

  const getOptions = (): any[] => {
    if (expressionAutocomplete.length > 0) {
      const lastNode = expressionAutocomplete[expressionAutocomplete.length - 1]
      if (isOperand(lastNode.code) || currentOperand.isComposing) {
        // If last node is an operand, we only show Prices, "(" and complex operands.
        const validOperands = operandsOptions.filter(
          operand => !(operand.operandType === EnumTipoOperando.BAS && operand.codOperando !== '(')
        )
        return pricesOptions.concat(validOperands)
      } else if (isPrice(lastNode.code)) {
        return operandsOptions
      } else return operandsOptions
    } else {
      const initialOperands = operandsOptions ? operandsOptions : []
      return pricesOptions.concat(initialOperands)
    }
  }

  const filter = createFilterOptions<any>()

  const GetCalculationTypeCompatibleOptions = () => {
    const compatibles = calculationTypeOptions.filter(
      p =>
        // p.priceType === priceType.toString() ||
        String(p.priceType) === String(priceType) ||
        p.priceType === enumKeyToValue(EnumPriceType, priceType) ||
        p.priceType === enumValueToKey(EnumPriceType, priceType)
    )[0]

    const compatibleOptions =
      calculationTypeOptions &&
      calculationTypeOptions.length > 0 &&
      calculationTypeOptions.filter(
        p =>
          // p.priceType === priceType.toString() ||
          String(p.priceType) === String(priceType) ||
          p.priceType === enumKeyToValue(EnumPriceType, priceType) ||
          p.priceType === enumValueToKey(EnumPriceType, priceType)
      )[0]

    if (compatibleOptions) {
      setCalculationTypeCompatibleOptions(compatibles.priceCalculationType)
    } else {
      setCalculationTypeCompatibleOptions([])
    }
  }

  useEffect(() => {
    onIsLoading && onIsLoading(isLoading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  return show ? (
    <>
      <Card elevation={elevation}>
        <div className={classes.buttons}>
          <Grid direction="column" container>
            {title ? (
              <Grid item>
                <Typography
                  variant="h5"
                  color="primary"
                  style={{ fontWeight: 'bold', opacity: 0.7 }}
                >
                  {title}
                </Typography>
              </Grid>
            ) : null}
            <Grid item>
              {loadingTypesOptions && <FontAwesomeIcon icon={faSpinner as IconProp} spin />}
              {loadingTypesOptionsFailed && (
                <Tooltip title={t('priceExpressionValidator:chips.loadingTypesOptions') ?? ''}>
                  <IconButton
                    color="secondary"
                    size="small"
                    onClick={() => {
                      getPriceTypeCalculationTypeCompatibility()
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
              )}
              {calculationTypeCompatibleOptions.length > 0 &&
                calculationTypeCompatibleOptions.map(
                  (typeOption: any, idx) =>
                    typeOption && (
                      <Chip
                        id="calculation-type-button"
                        size="small"
                        variant={enabled && !readOnly ? 'default' : 'outlined'}
                        key={`${typeOption}-chip-${idx}`}
                        className={clsx(classes.button, { [classes.buttonDisabled]: !enabled })}
                        label={getOptionLabel(typeOption)}
                        color={!inactive && typeOption === activeType ? 'primary' : 'default'}
                        clickable={enabled && !readOnly}
                        onClick={() => {
                          enabled && !readOnly && priceCalculationTypeChange(typeOption)
                          inputValue && inputValue.length > 0 && setIsLoading(true)
                        }}
                      />
                    )
                )}
            </Grid>
          </Grid>
        </div>
        <CardContent className={classes.expressionValidatorContainer}>
          <Grid
            direction="row"
            container
            wrap="wrap"
            spacing={spacing ? spacing : 2}
            justify={'flex-start'}
          >
            <Grid item style={{ width: '100%', maxWidth: '1000px' }}>
              <Autocomplete
                className={classes.autocomplete}
                freeSolo
                disabled={readOnly}
                inputValue={inputValue}
                onInputChange={(event, value, reason) => {
                  if (reason === 'input') {
                    event && event.preventDefault()
                    handleInputChange(event, value)
                  } else return
                }}
                value={currentNode}
                open={currentNode.length > 0 && open}
                onOpen={() => {
                  setOpen(true)
                }}
                onClose={() => setOpen(false)}
                loading={isLoading}
                onChange={handleChangeAutocomplete}
                onBlur={handleBlurAutocomplete}
                onFocus={handleFocusAutocomplete}
                multiple
                id="size-small-outlined"
                size="medium"
                options={getOptions()}
                filterOptions={(options, params) => {
                  const input = currentNode.length > 0 ? currentNode[0] : ''
                  const filtered = filter(options, { ...params, inputValue: input })
                  return filtered
                }}
                getOptionLabel={option => {
                  if (typeof option === 'string') {
                    return option
                  }
                  // Add "xxx" option created dynamically
                  if (option.inputValue) {
                    return option.inputValue
                  }
                  return option.code
                }}
                groupBy={option => option.category}
                renderTags={() => null}
                renderInput={params => (
                  <TextField
                    {...params}
                    inputRef={inputRef}
                    onSelect={updateCaretPosition}
                    variant="outlined"
                    helperText={getHelperText()}
                    FormHelperTextProps={{ style: { maxWidth: '300px' } }}
                    error={isValid !== undefined && !isValid}
                    label={t('priceExpressionValidator:input.label.expression')}
                    placeholder="Precio Unitario"
                    fullWidth
                    InputProps={
                      isLoading
                        ? {
                            ...params.InputProps,
                            startAdornment: (
                              <IconButton color={undefined} size="small">
                                <FontAwesomeIcon icon={faSpinner as IconProp} spin />
                              </IconButton>
                            )
                          }
                        : params.InputProps
                    }
                  />
                )}
              />
            </Grid>
            {showDates && (
              <Grid container item style={{ padding: 0 }} className={classes.dateForm}>
                <Form
                  sections={datesForm}
                  data={dates}
                  options={{
                    ...datesFormOptions,
                    analytics: {
                      customer: getCustomerDomain(),
                      view: t('translation:components.priceExpressionValidator.analytics')
                    }
                  }}
                />
              </Grid>
            )}
            {showPriceUnit && (
              <Grid item xs={3} style={{ minWidth: '200px', maxWidth: '300px' }}>
                <TextField
                  fullWidth
                  select
                  disabled={readOnly || disablePriceUnit}
                  InputProps={{
                    endAdornment:
                      priceExpressionFields.priceUnit && clearablePriceUnit ? (
                        <IconButton
                          style={{
                            visibility: priceExpressionFields.priceUnit ? 'visible' : 'hidden'
                          }}
                          //sx={{ visibility: value ? "visible" : "hidden" }}
                          onClick={() => handleClearClick(ClearableInputs.PRICE_UNIT)}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ) : null
                  }}
                  id="unit-input-validator"
                  value={priceExpressionFields.priceUnit ? priceExpressionFields.priceUnit : ''}
                  variant="outlined"
                  label={t('priceExpressionValidator:input.label.priceUnit')}
                  onChange={e =>
                    handlePriceUnitChange(enumValueToKey(EnumPrecioUnitario, e.target.value))
                  }
                >
                  {enumToSelectOptionArray(EnumPrecioUnitario, 'enums:CurrencyAndUnit').map(
                    (opt, idx) => (
                      <MenuItem key={`${opt.value}-${idx}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Grid>
            )}
            {showRounding && (
              <Grid item xs={2} style={{ minWidth: '200px', maxWidth: '300px' }}>
                <TextField
                  fullWidth
                  select
                  disabled={readOnly}
                  id="rounding-input-validator"
                  InputProps={{
                    endAdornment:
                      priceExpressionFields.rounding && clearableRounding ? (
                        <IconButton
                          style={{
                            visibility: priceExpressionFields.rounding ? 'visible' : 'hidden'
                          }}
                          //sx={{ visibility: value ? "visible" : "hidden" }}
                          onClick={() => handleClearClick(ClearableInputs.ROUNDING)}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ) : null
                  }}
                  value={priceExpressionFields.rounding ? priceExpressionFields.rounding : ''}
                  variant="outlined"
                  label={t('priceExpressionValidator:input.label.rounding')}
                  onChange={e => handleRounding(enumValueToKey(EnumRedondeo, e.target.value))}
                >
                  {enumToSelectOptionArray(EnumRedondeo, 'enums:EnumRedondeo').map((opt, idx) => (
                    <MenuItem key={`${opt.value}-${idx}`} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {showVersion && (
              <Grid
                item
                xs={modal ? 2 : versionsButton ? 2 : 2}
                zeroMinWidth
                container
                direction="row"
              >
                <Box
                  style={{
                    // minWidth: 50,
                    width: modal ? '100%' : versionsButton ? '100%' : '80px'
                  }}
                  alignItems={'center'}
                  justifyContent={'center'}
                  textAlign="center"
                  flexShrink={1}
                >
                  <TextField
                    id="version-input-validator"
                    value={priceExpressionFields.priceVersion}
                    disabled={true}
                    variant="outlined"
                    label={t('priceExpressionValidator:input.label.version')}
                    inputProps={{ style: { textAlign: 'center' } }}
                    InputProps={{
                      endAdornment: price && versionsButton && (
                        <InputAdornment position="end">
                          <Tooltip title="Ver Versiones" placement="bottom">
                            <IconButton
                              color="primary"
                              size="small"
                              edge="end"
                              onClick={() => {
                                history.push(NonAuthRoutes.versions, {
                                  price: { ...price, commodity: commodity }
                                })
                              }}
                            >
                              <Info fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Validation Helper - Shows when expression needs validation */}
      {showValidationHelper && isExpressionModified && (!inputValue || !isValid) && !isLoading && (
        <Card elevation={0} style={{ paddingRight: 12, backgroundColor: '#e3f2fd' }}>
          <CardContent style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <InfoOutlined color="primary" />
            <div style={{ flex: 1 }}>
              <Typography variant="body2" color="textSecondary">
                {t('priceExpressionValidator:validationHelper')}
              </Typography>
            </div>
            <Button
              ref={validateButtonRef}
              variant="outlined"
              color="primary"
              size="small"
              disabled={isLoading || !inputValue}
              onClick={() => {
                // Focus this button to trigger blur on expression input
                validateButtonRef.current?.focus()
              }}
            >
              {t('priceExpressionValidator:validateExpression')}
            </Button>
          </CardContent>
        </Card>
      )}
    </>
  ) : (
    <></>
  )
}

export default PriceExpressionValidator
